<template>
  <div class="update-payment-plan">
    <div
      class="payment-boxes"
      :class="smallScreen ? 'ma-0 mx-2' : 'mx-2 mt-2 mb-4'"
    >
      <PlanSelectionView
        v-if="activeStep === UpdatePaymentPlanSteps.SELECT_PLAN"
        :current-tier-plan-id="currentTierPlanId"
        :has-starter-options="hasStarterOptions"
        :all-tier-plans="allTierPlans"
        :subscription-tier="subscription_tier"
        :gm-type="gm_type"
        :selected-plan="selectedPlan"
        :initial-plan-id="selectedPlanId"
        @update:selectedPlanId="val => (selectedPlanId = val)"
        @plan-selected="choosePlan"
      />

      <div
        v-if="activeStep === UpdatePaymentPlanSteps.SELECT_PAYMENT_METHOD"
        class="update-step"
      >
        <PaymentMethodSelection
          v-if="errorNoCard || selectPaymentMethod || pendingPayment"
          :startGate="startGate"
          :errorNoCard
          :upgradePrice="upgradePrice"
          :onSuccessPayment="handleSuccess"
        />
        <UpdatePlanCheckout
          v-else
          :loading="step2Loading"
          :upgradePrice="upgradePrice"
          :manualCheckout="manualCheckout"
          @switch-to-yearly="localSwitchToYearly"
        />
      </div>

      <SuccessPaymentView
        v-if="activeStep === UpdatePaymentPlanSteps.SUCCESS"
        :current-plan="currentPlan"
        :selected-pay-plan-full-name="selectedPayPlanFullName"
        :stripe-downgrading="stripeDowngrading"
      />

      <!-- TODO: Test this -->
      <FailedPaymentView
        v-if="activeStep === UpdatePaymentPlanSteps.ERROR"
        :price-error="priceError"
        :stripe-error="stripeError"
      />
    </div>

    <portal to="update_payment_dialog_actions">
      <GwBtn
        v-if="activeStep === UpdatePaymentPlanSteps.SELECT_PLAN"
        variant="btn_large"
        data-tst="btn_cancel"
        class="mr-auto"
        @click.stop="close()"
        >{{ $t('btn.cancel') }}</GwBtn
      >
      <div v-if="activeStep === UpdatePaymentPlanSteps.SELECT_PAYMENT_METHOD">
        <OpenChatButton v-if="manualCheckout" />
        <GwBtn
          v-else-if="selectPaymentMethod || errorNoCard"
          variant="btn_large"
          class="ml-auto"
          data-tst="btn_back"
          @click="resetPayment"
          >{{ $t('btn.back') }}</GwBtn
        >
        <GwBtn
          v-else
          variant="btn_large"
          data-tst="btn_back"
          class="mr-auto"
          @click.stop="activeStep = UpdatePaymentPlanSteps.SELECT_PLAN"
          >{{ $t('btn.back') }}</GwBtn
        >
      </div>

      <GwBtn
        v-if="
          activeStep === UpdatePaymentPlanSteps.SELECT_PLAN && currentTierPlan.id !== selectedPlanId
        "
        variant="btn_primary-v2-large"
        class="ml-auto"
        style="--surface-bg: var(--app-clr-primary); --text-clr: var(--clr-white-0)"
        data-tst="btn_continue"
        :disabled="selectedPlanId === 'none'"
        :loading="btnLoading"
        @click="setSecondStep"
        >{{ $t('btn.continue') }}</GwBtn
      >
      <CheckoutControls
        v-if="showCheckoutControls"
        :view="view"
        :downgrading
        :newCardLoading="btnLoading"
        :checkoutLoading="checkoutLoading"
        @select-payment-method="selectPaymentMethod = true"
        @checkout="update"
        @checkoutWithCrypto="updateWithCrypto"
      />
      <GwBtn
        v-show="activeStep !== UpdatePaymentPlanSteps.ERROR"
        v-if="
          activeStep === UpdatePaymentPlanSteps.SUCCESS ||
          currentTierPlan.id === selectedPlanId ||
          (manualCheckout && activeStep === UpdatePaymentPlanSteps.SELECT_PAYMENT_METHOD)
        "
        variant="btn_large"
        class="ml-auto"
        data-tst="btn_close"
        :loading="btnLoading"
        @click="close()"
        >{{ $t('btn.close') }}</GwBtn
      >
      <div
        v-if="activeStep === UpdatePaymentPlanSteps.ERROR"
        class="w-100 d-flex justify-between"
      >
        <OpenChatButton />
        <GwBtn
          variant="btn_primary-v2-large"
          style="--surface-bg: var(--app-clr-primary); --text-clr: var(--clr-white-0)"
          class="ml-auto"
          data-tst="btn_try_again"
          @click="activeStep = UpdatePaymentPlanSteps.SELECT_PLAN"
          >{{ $t('btn.try_again') }}</GwBtn
        >
      </div>
    </portal>
    <portal to="update_payment_confirm_btn">
      <CheckoutControls
        v-if="showCheckoutControls"
        :downgrading
        :newCardLoading="btnLoading"
        :checkoutLoading="checkoutLoading"
        :upgradePrice="upgradePrice"
        @select-payment-method="selectPaymentMethod = true"
        @checkout="update"
        @checkoutWithCrypto="updateWithCrypto"
      />
      <div
        v-else-if="manualCheckout"
        class="d-flex w-100 justify-between"
      >
        <OpenChatButton />
        <GwBtn
          variant="btn_large"
          class="ml-auto"
          data-tst="btn_return"
          @click="goBack"
          >{{ $t('btn.return') }}</GwBtn
        >
      </div>
      <GwBtn
        v-else
        variant="btn_large"
        class="ml-auto"
        data-tst="btn_return"
        @click="goBack"
        >{{ $t('btn.return') }}</GwBtn
      >
    </portal>
  </div>
</template>

<script>
import { gtm_sub_upgrade, gtm_sub_upgrade_dialog_events, gtm_sub_upgrade_thx } from '@/helpers/gtm'

import format_mix from '@/mixins/format_mix'
import payment_mix from '@/mixins/payment_mix'
import links_mix from '@/mixins/links_mix'
import GwBtn from '@/components/lib/GwBtn/GwBtn.vue'
import { updateSubscription } from '@/helpers/paymentHelpers'
import CheckoutControls from '@/components/Dialogs/UpdatePaymentPlanDialog/CheckoutControls.vue'
import OpenChatButton from '@/components/Payments/OpenChatButton.vue'
import PlanSelectionView from '@/components/Dialogs/UpdatePaymentPlanDialog/PlanSelectionView.vue'
import SuccessPaymentView from '@/components/Dialogs/UpdatePaymentPlanDialog/SuccessPaymentView.vue'
import FailedPaymentView from '@/components/Dialogs/UpdatePaymentPlanDialog/FailedPaymentView.vue'
import UpdatePlanCheckout from '@/components/Dialogs/UpdatePaymentPlanDialog/UpdatePlanCheckout'
import PaymentMethodSelection from '@/components/Payments/PaymentMethodSelection'

export default {
  name: 'UpdatePaymentPlan',
  components: {
    OpenChatButton,
    CheckoutControls,
    GwBtn,
    PaymentMethodSelection,
    UpdatePlanCheckout,
    PlanSelectionView,
    SuccessPaymentView,
    FailedPaymentView,
  },
  mixins: [format_mix, payment_mix, links_mix],
  props: {
    startAtStep: Number,
    /**
     * @type {String}
     * @values 'dialog', 'page'
     */
    view: { type: String, default: 'page' },
    hasEbMessage: { type: Boolean, default: true },
  },
  emits: ['success', 'error'],
  data() {
    return {
      selectedPlanId: 'none',
      /**
       * 1 - select_plan
       * 2 - select_payment_method
       * 3 - success
       * 4 - error
       */
      activeStep: 1,
      UpdatePaymentPlanSteps: {
        SELECT_PLAN: 1,
        SELECT_PAYMENT_METHOD: 2,
        SUCCESS: 3,
        ERROR: 4,
      },
      step2Loading: false,
      btnLoading: false,
      checkoutLoading: false,
      stripeUpgradeInvoice: { total: 0, user_in_trial: false },
      upgradePrice: 0,
      stripeDowngrading: false,
      stripeError: true,
      priceError: null,
      errorNoCard: false,
      selectPaymentMethod: false,
      startGate: 'card',
    }
  },
  created() {
    this.selectedPlanId = this.selectedTierPlan.id
    const { step_paypal, ...query } = this.$route.query
    if (this.startAtStep || step_paypal) {
      this.activeStep = this.startAtStep || Number(step_paypal)
      if (this.activeStep === this.UpdatePaymentPlanSteps.SELECT_PAYMENT_METHOD) {
        this.setSecondStep()
      }
    }
    if (step_paypal) {
      this.$router.replace({ query })
    }

    const isPendingPayment = this.$route.query.pending_payment === 'true'

    if (isPendingPayment) {
      this.$router.replace({ query: { ...this.$route.query, pending_payment: undefined } })
      this.selectPaymentMethod = true
    }
  },
  methods: {
    updateWithCrypto() {
      this.startGate = 'crypto'
      this.selectPaymentMethod = true
      this.errorNoCard = false
      this.activeStep = this.UpdatePaymentPlanSteps.SELECT_PAYMENT_METHOD
    },
    resetPayment() {
      this.startGate = 'card'
      if (this.$route.query.gate) {
        this.goBack()
        return
      }
      this.selectPaymentMethod = false
      this.errorNoCard = false
      this.activeStep = this.UpdatePaymentPlanSteps.SELECT_PAYMENT_METHOD
    },
    choosePlan(item) {
      const { plan } = item
      this.selectedPlanId = item.id

      this.$store.dispatch('router_update_query', {
        query: {
          gm_type: plan.type.toLowerCase(),
          gm_tier: plan.tier.toLowerCase(),
          gm_interval: plan.interval.toLowerCase(),
        },
        push: false,
      })

      gtm_sub_upgrade_dialog_events({
        subpage: this.$store.state.router.from_path_name || this.$route.name,
        action: 'pick',
        subscription_game: plan.type,
        type: plan.tier.toLowerCase(),
        paymentType: plan.interval.toLowerCase(),
      })
    },
    localSwitchToYearly() {
      const yearlyPlan = this.selectedTierPlans.find(i => i.time === 'year')
      this.choosePlan(yearlyPlan)
      this.setSecondStep()
    },
    async fetchUpgradePrice() {
      if (this.selectedTierPlan.id === this.currentTierPlan.id || this.manualCheckout) {
        this.upgradePrice = 0
        return true
      }
      if (this.isCurrentTierPlanFree) {
        this.upgradePrice = this.selectedPlan.price
      } else if (this.currentPlan.provider === 'Adyen') {
        const endpoint = `payments/subscription/${this.currentSub.public_id}/plan/${this.selectedPlan.id}/change-price/`
        const { response } = await this.$store.dispatch('api_call', {
          method: 'get',
          endpoint,
        })
        if (!response?.data) {
          this.handleUpgradePriceError(response?.error)
          return false
        }
        this.upgradePrice = response.data.amount_to_pay
      } else {
        const params = {
          plan_id: this.newStripePlanID,
          public_id: this.currentSub.public_id,
        }
        const res = await this.$store.dispatch('api_call', {
          method: 'GET',
          endpoint: 'payments/subscription/stripe/invoice/',
          params,
        })
        if (res?.response?.status === 200) {
          this.stripeUpgradeInvoice = res.response.data
        } else {
          return false
        }
        this.upgradePrice = this.stripeUpgradeInvoice.total
      }
      return true
    },
    async setSecondStep() {
      this.stripeError = null

      if (this.selectedPlanId === this.currentTierPlan?.id) {
        return
      }

      if (this.manualCheckout) {
        this.activeStep = this.UpdatePaymentPlanSteps.SELECT_PAYMENT_METHOD
        return
      }
      this.btnLoading = true
      this.step2Loading = true
      if (!(await this.fetchUpgradePrice())) {
        this.handleError()
      }
      this.btnLoading = false
      this.step2Loading = false
      this.activeStep = this.UpdatePaymentPlanSteps.SELECT_PAYMENT_METHOD
      this.setGtmStep()
    },
    setGtmStep(step = this.UpdatePaymentPlanSteps.SELECT_PLAN) {
      const plan = this.selectedPlan
      gtm_sub_upgrade({
        step,
        currencyCode: 'USD',
        game: plan.type,
        type: plan.tier.toLowerCase(),
        paymentType: plan.interval.toLowerCase(),
        price: plan.price,
      })
    },
    update() {
      this.startGate = 'card'
      if (this.selectedPlanId === this.currentTierPlan?.id) {
        this.close()
        return
      }
      this.setGtmStep(this.UpdatePaymentPlanSteps.SELECT_PAYMENT_METHOD)

      let provider = this.currentPlan.provider
      if (provider === 'Gtowizard') {
        this.allSubs.forEach(sub => {
          if (sub.plan?.tier !== 'FREE') {
            provider = sub.plan.provider
          }
        })
      }

      if (provider === 'Adyen') {
        this.updateAdyen()
      } else if (provider === 'Paypal') {
        this.updatePaypal()
      } else if (provider === 'Stripe') {
        this.updateStripe()
      }
    },
    close() {
      this.$store.dispatch('router_rm_dialog', 'update-payment-plan-dialog')
      this.$router.replace({ query: {} })

      gtm_sub_upgrade_dialog_events({
        subpage: this.$store.state.router.from_path_name || this.$route.name,
        action: 'close',
        // subscription_game: '',
        // type: '',
        // paymentType: '',
      })
    },
    async updateAdyen() {
      this.checkoutLoading = true
      const plan = this.selectedPlan.id
      const isCurrentFreePlan = this.currentPlan.tier === 'FREE'
      const res = await this.$store.dispatch('api_call', {
        method: isCurrentFreePlan ? 'post' : 'patch',
        endpoint: `payments/subscription/adyen/${isCurrentFreePlan ? '' : `${this.currentSub.public_id}/`}`,
        data: {
          plan,
          use_card: true,
          country_code: 'US',
        },
      })
      if (res?.response?.status === 201 || res?.response?.status === 200) {
        await updateSubscription(
          this.selectedPlan,
          this.downgrading ? 'downgrade' : 'upgrade',
          () => {
            this.handleSuccess({
              payment_provider: 'adyen',
              payment_method: 'card',
            })
            this.checkoutLoading = false
          },
        )
      } else {
        this.errorNoCard = true
        this.checkoutLoading = false
      }
    },
    async updateStripe() {
      this.stripeError = null

      this.stripeDowngrading = this.currentTierPlan.rank > this.selectedPlan.rank
      this.btnLoading = true
      const res = await this.$store.dispatch('api_call', {
        method: 'PATCH',
        endpoint: 'payments/subscription/stripe/',
        data: {
          plan_id: this.newStripePlanID,
          public_id: this.currentSub.public_id,
        },
      })

      if (res?.response?.status === 200 || res?.response?.status === 204) {
        await this.$store.dispatch('api_call', {
          method: 'get',
          endpoint: 'account/settings/',
          hasCancel: false,
          mutation: 'api_account_settings',
          action: 'updateUserInfo',
        })
        this.$store.dispatch('gameformats_reinitialize_after_tier_update')

        this.handleSuccess({
          payment_provider: 'stripe',
          payment_method: 'card',
        })
      } else {
        const error = res?.error
        const status = res?.error?.response?.data?.status
        const message = res?.error?.response?.data?.message
        this.stripeError = { error, status, message }

        this.handleError(res)
      }
    },
    async updatePaypal() {
      this.btnLoading = true
      const app_url =
        import.meta.env.VITE_ENV === 'development'
          ? 'https://localdevnonexistenturl.com'
          : this.production
            ? 'https://gtowizard.com'
            : window.location.origin

      const plan = this.selectedPlan
      const gm_type = plan.type.toLowerCase()
      const gm_tier = plan.tier.toLowerCase()
      const gm_interval = plan.interval.toLowerCase()
      const planParams = `gm_type=${gm_type}&gm_tier=${gm_tier}&gm_interval=${gm_interval}`

      const data = {
        plan_id: this.newPaypalPlanID,
        public_id: this.currentSub.public_id,
        return_url: `${app_url}/user/subscription?dialogs=update-payment-plan-dialog&step_paypal=3&${planParams}`,
        cancel_url: `${app_url}/user/subscription?dialogs=update-payment-plan-dialog&step_paypal=4&${planParams}`,
      }
      // user/subscription?dialogs=update-payment-plan-dialog&step_paypal=3&gm_type=cash&gm_tier=starter&gm_interval=monthly
      // user/subscription?dialogs=update-payment-plan-dialog&step_paypal=4&gm_type=cash&gm_tier=starter&gm_interval=monthly

      const res = await this.$store.dispatch('api_call', {
        method: 'PATCH',
        endpoint: 'payments/subscription/paypal/',
        data,
      })
      if (res?.response?.status === 200) {
        const confirm_link = res.response.data?.confirm_link
        if (confirm_link) {
          window.location.replace(confirm_link)
          return
        }
        this.handleSuccess({
          payment_provider: 'paypal',
          payment_method: 'paypal',
        })
        this.$store.dispatch('router_reset_dialogs')
      } else {
        this.handleError()
      }
    },

    handleSuccess({ payment_provider, payment_method }) {
      const plan = this.selectedPlan

      gtm_sub_upgrade_thx({
        currencyCode: 'USD',
        // TODO: Why it's hardcoded to paypal?
        transaction_id: this.newPaypalPlanID,
        transaction_revenue: plan.price,
        game: plan.type,
        type: plan.tier.toLowerCase(),
        paymentType: plan.interval.toLowerCase(),
        price: plan.price,
      })

      this.btnLoading = false

      if (this.view === 'dialog') {
        this.activeStep = this.UpdatePaymentPlanSteps.SUCCESS
      } else {
        this.$router.push({
          name: 'SubscriptionPaymentConfirmation',
          query: {
            payment_provider,
            payment_method,
            dialogs: '',
            gm_type: plan.type.toLowerCase(),
            gm_tier: plan.tier.toLowerCase(),
            gm_interval: plan.interval?.toLowerCase(),
          },
        })
      }
    },
    handleError() {
      this.$emit('error', { payment_provider: this.payment_provider })
      this.btnLoading = false
      this.activeStep = this.UpdatePaymentPlanSteps.ERROR
    },
    handleUpgradePriceError(error) {
      this.priceError = error || true
      this.btnLoading = false
      this.activeStep = this.UpdatePaymentPlanSteps.ERROR
    },
  },
  computed: {
    pendingPayment() {
      return this.$route.query.pending_payment === 'true'
    },
    showCheckoutControls() {
      return (
        this.activeStep === this.UpdatePaymentPlanSteps.SELECT_PAYMENT_METHOD &&
        this.currentTierPlan.id !== this.selectedPlanId &&
        !this.manualCheckout &&
        !(this.selectPaymentMethod || this.errorNoCard)
      )
    },
    selectedPayPlanFullName() {
      const { plan } = this.selectedTierPlan
      const res = `${this.$t(`gametypes.groups.${plan.type.toLowerCase()}_long`)} / ${
        this.selectedTierPlan.full_name
      }`
      return res
    },
    production() {
      return import.meta.env.VITE_ENV === 'production' && import.meta.env.VITE_BRANCH === 'master'
    },
    user_info() {
      const { user_info } = this.$store.getters
      return user_info
    },
    subscription_game() {
      return this.user_info.subscription_game
    },
    subscription_tier() {
      return this.user_info.subscription_tier
    },
    hasStarterOptions() {
      return this.$route.name !== 'UserAccount' && !this.gmStraddle
    },
    newStripePlanID() {
      const plan_id = this.selectedPlan.external_plans?.stripe?.external_plan_id
      return plan_id
    },
    newPaypalPlanID() {
      const plan_id = this.selectedPlan.external_plans?.paypal?.external_plan_id
      return plan_id
    },
    currentTierPlanId() {
      const res = this.currentTierPlan?.id
      return res
    },
    manualCheckout() {
      if (this.stripeUpgradeInvoice.user_in_trial) {
        return true
      }

      // starterAnnuallyToPremiumMonthly
      const providerGtowizard = this.currentPlan.provider === 'Gtowizard'
      const isFreePlan = this.currentPlan.tier === 'FREE'

      const starterAnnuallyToPremiumMonthly =
        (this.currentTierPlan?.id || '').includes('starter_yearly') &&
        (this.selectedTierPlan?.id || '').includes('premium_monthly')

      const starterAnnuallyToEliteMonthly =
        (this.currentTierPlan?.id || '').includes('starter_yearly') &&
        (this.selectedTierPlan?.id || '').includes('elite_monthly')

      const premiumAnnuallyToEliteMonthly =
        (this.currentTierPlan?.id || '').includes('premium_yearly') &&
        (this.selectedTierPlan?.id || '').includes('elite_monthly')

      return (
        !isFreePlan &&
        (providerGtowizard ||
          starterAnnuallyToPremiumMonthly ||
          starterAnnuallyToEliteMonthly ||
          premiumAnnuallyToEliteMonthly)
      )
    },
    smallScreen() {
      const { globals_window_size } = this.$store.getters
      return globals_window_size.width < 768
    },
    downgrading() {
      return this.currentTierPlan.rank > this.selectedTierPlan.rank
    },
  },
}
</script>

<style lang="scss" scoped>
.update-dialog {
  width: 540px;
  min-height: 664px;
  transition: 0.4s ease;
}
.update-step {
  width: 100%;
  &_step {
    &_title {
      font-size: 1.5rem;
      font-weight: 700;
      white-space: nowrap;
    }
    &_sub {
      display: flex;
      font-size: 1rem;
    }
  }
}
.update-steps {
  font-size: 0.8rem;
  color: var(--clr-grey-2);
  padding: 0 1rem;
}
.switch-to-yearly {
  cursor: pointer;
  color: var(--clr-primary);
  &:hover {
    text-decoration: underline;
  }
}

.tier-tag {
  border-radius: 4px;
  font-weight: 700;
  font-size: 1.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  align-self: flex-start;
  flex: 0;
  &_trial {
    color: var(--clr-white-0);
  }
  &_starter {
    color: var(--clr-blue);
  }
  &_premium {
    color: var(--clr-yellow);
  }
  &_icon {
    font-size: 1.5rem;
    margin: 0 0.5rem 0 0;
    color: currentColor;
  }
}
</style>
